import {
  systemFoodDictionaryControllerSearchFoods,
  systemFoodDictionaryControllerCreateFood,
  systemFoodDictionaryControllerGetUsage,
  systemFoodDictionaryControllerGetFoodById,
  systemFoodDictionaryControllerUpdateCertified,
  systemFoodDictionaryControllerUpdateFoodGroup,
  systemFoodDictionaryControllerUpdateFood,
  systemFoodDictionaryControllerDeleteFood,
  systemFoodDictionaryControllerAddServing,
  systemFoodDictionaryControllerUpdateServing,
  systemFoodDictionaryControllerRemoveServing,
  systemFoodDictionaryControllerGetNutrientDefinitions,
} from "../../api/generated";

// SYSTEM_ADMIN food-dictionary admin — backed by SystemFoodDictionaryController
// (/system/food-dictionary/*). Migrated to the OpenAPI-generated client: auth + timezone
// come from configureApi's interceptor (per-call token reads removed). Every endpoint here
// returns its DTO / page UN-enveloped, so `data` == the old axios `response.data`, and each
// function returns {data} to preserve callers reading `res.data(.content/.totalElements)`.
//
// The catalog is global and public: entries are loaded by the offline importer
// (progress-server/scripts/import/foodsdictionary) and only reviewed/corrected here. There is no
// enrichment pipeline, no review queue and no publish/visibility flow — those endpoints are gone.
//
// NOTE: the springdoc spec models this controller's list endpoint with FLAT page/size query
// params (not a nested Pageable), so the search list CAN be migrated.

export const searchFoodDictionary = async ({
  page = 0,
  size = 20,
  keyword = "",
  certified = null,
  scope = null,
  companyId = null,
} = {}) => {
  const query = {page, size};
  if (keyword?.trim()) query.keyword = keyword.trim();
  if (certified !== null && certified !== undefined) query.certified = certified;
  // Tenancy lenses. `scope` narrows to SYSTEM / COMPANY / TRAINEE; `companyId` picks one company
  // out of the company-owned foods, which is how an admin reviews a single tenant's additions.
  if (scope) query.scope = scope;
  if (companyId) query.companyId = companyId;
  const {data, error} = await systemFoodDictionaryControllerSearchFoods({query});
  if (error) throw error;
  return {data};
};

export const getFoodDictionaryById = async (id) => {
  const {data, error} = await systemFoodDictionaryControllerGetFoodById({path: {id}});
  if (error) throw error;
  return {data};
};

export const updateFoodDictionaryCertified = async (id, certified) => {
  const {data, error} = await systemFoodDictionaryControllerUpdateCertified({
    path: {id},
    body: {certified},
  });
  if (error) throw error;
  return {data};
};

/**
 * Correct the group an LLM labeller put on a food, or clear it.
 *
 * <p>`foodGroup: null` is a VALUE here, not a missing argument — the labeller's queue is every food
 * whose group is null, so clearing hands the food back to it. That is the honest answer when the
 * model is wrong and the person looking does not know the right one either, and it is why this sends
 * the key explicitly rather than omitting it.
 */
export const updateFoodDictionaryFoodGroup = async (id, foodGroup) => {
  const {data, error} = await systemFoodDictionaryControllerUpdateFoodGroup({
    path: {id},
    body: {foodGroup: foodGroup ?? null},
  });
  if (error) throw error;
  return {data};
};

export const updateFoodDictionary = async (id, payload) => {
  const {data, error} = await systemFoodDictionaryControllerUpdateFood({
    path: {id},
    body: payload,
  });
  if (error) throw error;
  return {data};
};

/**
 * Delete a food and remove it from every menu on the platform that plans it.
 *
 * <p>Without `confirm` the server answers 409 and returns the usage counts rather than deleting, so the
 * caller can show what is about to happen.
 */
export const deleteFoodDictionary = async (id, {confirm = false} = {}) => {
  const {data, error} = await systemFoodDictionaryControllerDeleteFood({
    path: {id},
    query: {confirm},
  });
  if (error) throw error;
  return {data};
};

/**
 * Platform-wide usage for a food — every company's menus, not one.
 *
 * <p>Raw call for the same reason as above: newer than the snapshot. Deleting or changing a system
 * food touches every company at once, which is exactly the number worth showing before it happens.
 */
/**
 * Create a system food — un-owned, so everyone on the platform can find it.
 *
 * <p>The scope is not in the payload and cannot be: the endpoint decides it from who is calling.
 */
export const createFoodDictionary = async (payload) => {
  const {data, error} = await systemFoodDictionaryControllerCreateFood({body: payload});
  if (error) throw error;
  return {data};
};

/**
 * Platform-wide usage for a food — every company's menus, not one.
 *
 * <p>Deleting or changing a system food touches every company at once, which is exactly the number
 * worth putting in front of someone before it happens.
 */
export const getFoodDictionaryUsage = async (id) => {
  const {data, error} = await systemFoodDictionaryControllerGetUsage({path: {id}});
  if (error) throw error;
  return {data};
};

export const addFoodDictionaryServing = async (foodId, payload) => {
  const {data, error} = await systemFoodDictionaryControllerAddServing({
    path: {id: foodId},
    body: payload,
  });
  if (error) throw error;
  return {data};
};

export const updateFoodDictionaryServing = async (foodId, servingId, payload) => {
  const {data, error} = await systemFoodDictionaryControllerUpdateServing({
    path: {id: foodId, servingId},
    body: payload,
  });
  if (error) throw error;
  return {data};
};

export const removeFoodDictionaryServing = async (foodId, servingId) => {
  const {data, error} = await systemFoodDictionaryControllerRemoveServing({
    path: {id: foodId, servingId},
  });
  if (error) throw error;
  return {data};
};

export const getNutrientDefinitions = async () => {
  const {data, error} = await systemFoodDictionaryControllerGetNutrientDefinitions();
  if (error) throw error;
  return {data};
};
